import React, { useState, useMemo } from "react";
import { GameCard } from "@/components/GameCard";
import { AdBanner } from "@/components/AdBanner";
import Seo from "@/components/Seo";
import { gamesByCategory, QUIZZES, CATEGORIES } from "@/data/games";
import { Gamepad2, Brain, HelpCircle } from "lucide-react";

const META = {
  arcade: { icon: Gamepad2, title: "Arcade Games", desc: "Classic arcade action — Neon Snake, 2048 Rush, Memory Match, Connect Four and more. Free to play in your browser." },
  brain: { icon: Brain, title: "Brain Teasers & IQ Tests", desc: "Sharpen your mind with IQ tests, memory challenges, typing & reaction tests, sliding puzzles and logic games." },
  quiz: { icon: HelpCircle, title: "Personality & Fun Quizzes", desc: "BuzzFeed-style quizzes — relationship quizzes, anime quizzes, IQ personality tests and more. Find your result and share it!" },
};

export default function CategoryPage({ category }) {
  const meta = META[category];
  const Icon = meta.icon;
  const color = CATEGORIES[category].color;
  const items = category === "quiz" ? QUIZZES : gamesByCategory(category);
  const [filter, setFilter] = useState("All");

  const tags = useMemo(() => {
    if (category !== "quiz") return [];
    return ["All", ...Array.from(new Set(QUIZZES.map((q) => q.tag).filter(Boolean)))];
  }, [category]);

  const shown = category === "quiz" && filter !== "All" ? items.filter((q) => q.tag === filter) : items;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-12" data-testid={`category-page-${category}`}>
      <Seo title={meta.title} description={meta.desc} />
      <div className="flex items-center gap-3 mb-2">
        <Icon className="w-9 h-9" style={{ color }} />
        <h1 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tighter">{meta.title}</h1>
      </div>
      <p className="text-zinc-400 max-w-2xl mb-8">{meta.desc}</p>

      {category === "quiz" && (
        <div className="flex flex-wrap gap-2 mb-10" data-testid="quiz-filters">
          {tags.map((t) => (
            <button key={t} onClick={() => setFilter(t)} data-testid={`quiz-filter-${t.toLowerCase()}`}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest rounded-full border-2 transition-colors ${
                filter === t ? "bg-neon-pink text-white border-neon-pink" : "bg-transparent text-zinc-300 border-zinc-700 hover:border-white"
              }`}>
              {t}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {shown.map((g, i) => <GameCard key={g.id} item={g} index={i} />)}
      </div>

      <AdBanner slot={`category-${category}`} className="mt-12" />
    </div>
  );
}
